import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { MenuPath, VarCard } from '../../touchpose/ui';
import {
  H2,
  H3,
  Body,
  DocHero,
  DocImage,
  KeySettings,
  QuadCages,
  SequencerNote,
  DocFooter, Steps
} from './shared';
import alembicImportP1 from '../../../assets/images/squarebit-subdivs/docs/alembic-import-p1.png';
import alembicImportScale from '../../../assets/images/squarebit-subdivs/docs/alembic-import-scale.png';
import addComponentImg from '../../../assets/images/squarebit-subdivs/docs/adding-geometry-cache-component.png';
import geometryCacheSettingsImg from '../../../assets/images/squarebit-subdivs/docs/geometry-cache-settings.png';

const M = (s: string) => <MenuPath>{s}</MenuPath>;

// The Unreal component name is never translated. String-only props (image alt
// text, video caption) take it as an interpolated {{comp}} value so it stays
// outside the translated copy there too.
const COMPONENT = 'Squarebit Subdiv Geometry Cache Component';

const GeometryCachesDoc: React.FC = () => {
  const { t } = useTranslation();

  return (
    <article className="font-body text-content-primary max-w-[760px]">
      <DocHero
        title={t('subdivsDocs.layout.geometryCaches')}
        intro={
          <Trans
            i18nKey="subdivsDocs.geometryCaches.intro"
            components={{ comp: <>{COMPONENT}</> }}
          />
        }
        videoSrc="/videos/subdiv-geometry-cache-setup.mp4"
        videoCaption={t('subdivsDocs.geometryCaches.videoCaption', {
          comp: COMPONENT
        })}
      />

      <H2 id="import-settings">{t('subdivsDocs.layout.importSettings')}</H2>

      <H3 id="import-alembic">Alembic</H3>
      <DocImage
        src={alembicImportP1}
        alt={t('subdivsDocs.geometryCaches.alembicImportAlt', {
          setting: 'Store Imported Vertex Numbers'
        })}
      />
      <Body>
        <Trans
          i18nKey="subdivsDocs.geometryCaches.alembicBody1"
          components={{
            gc: <strong>Geometry Cache</strong>,
            sm: <strong>Static Mesh</strong>,
            adv: M('Advanced'),
            sivn: <strong>Store Imported Vertex Numbers</strong>
          }}
        />
      </Body>
      <DocImage
        src={alembicImportScale}
        alt={t('subdivsDocs.geometryCaches.alembicScaleAlt')}
      />
      <Body>{t('subdivsDocs.geometryCaches.alembicBody2')}</Body>

      <H3 id="import-usd">USD</H3>
      <Body>{t('subdivsDocs.geometryCaches.usdBody')}</Body>

      <H2 id="adding">{t('subdivsDocs.layout.adding')}</H2>
      <Steps>
        <><Trans
            i18nKey="subdivsDocs.geometryCaches.step1"
            components={{ abc: M('.abc'), usd: M('.usd') }}
          /></>
        <>{t('subdivsDocs.geometryCaches.step2')}</>
        <><Trans
            i18nKey="subdivsDocs.geometryCaches.step3"
            components={{
              add: M('Add'),
              q: <em>&ldquo;Squarebit Subdiv Geometry Cache&rdquo;</em>,
              comp: <strong>{COMPONENT}</strong>
            }}
          />
          <DocImage
            src={addComponentImg}
            alt={t('subdivsDocs.geometryCaches.addComponentAlt', {
              comp: COMPONENT
            })}
          /></>
        <>{t('subdivsDocs.geometryCaches.step4')}</>
      </Steps>

      <KeySettings image={geometryCacheSettingsImg} />

      <H3>{t('subdivsDocs.geometryCaches.gcTitle')}</H3>
      <VarCard name="Support Dynamic Topology" type="bool" example="true">
        {t('subdivsDocs.geometryCaches.supportDynamicTopology')}
      </VarCard>

      <QuadCages alembicTip naniteWarning />
      <SequencerNote />
      <DocFooter current="/squarebit-subdivs/docs/geometry-caches" />
    </article>
  );
};

export default GeometryCachesDoc;
